// Types and adapter for the Clinical Review result value (design: Clinical Assessment).
//
// The ClinicalReview action produces a per-building `clinical_review_result` — the AI-derived
// admission criteria matched against that building's packet. Its content IS the criteria array
// (no wrapper object), so the value follows the same family convention as `payer_candidates` and
// `attachments_processed`: the produced value is its list. It is a committed, group-stamped
// workflow value: the result is committed before the coordinator reviews it, so we read it off the
// workflow (via `valueFor`, group-scoped to the building's action) rather than off the action's
// uncommitted `outputs` — the same source the other fanout result renderers read from
// (`CompletedResult`, `FanoutPanel`). This module owns that read and narrows it to a typed shape,
// mirroring how `eligibilityData.ts` owns the PayerSearch/BenefitsEligibility payloads.
//
// Value NAMES are snake_case and read verbatim (olio-api leaves them alone); the value CONTENT is
// camelCased by olio-api's OliveBranch, so every field below is camelCase.

import Workflow, { WorkflowAction } from 'models/Workflow';

// The `clinical_review_result` value written by the ClinicalReview action, group-stamped per building.
export const CLINICAL_REVIEW_RESULT_VALUE = 'clinical_review_result';

// One matched admission criteria in a clinical review result — an admission criteria the AI matched
// against the packet. Named `…Criteria` to follow the API's model naming (ai_criteria /
// ai_job_criteria), not the FE's older `…Criterion`. Keys arrive camelCase — the API response is
// camelCased by OliveBranch. `criterion` (the matched text) mirrors the API column and follows it
// if/when that column is renamed to `label`. `order` places this criteria within its category, and
// `category.order` places the category among its siblings — both drive display order (see
// `orderClinicalReviewCriteria`) rather than relying on the payload's array position.
//
// `category.id` is optional: the api publishes it from the frozen category snapshot on the
// ai_job_criterion, and that snapshot only carries an id once OLIO-2697's api change is deployed
// (there is deliberately no backfill, so older rows never gain one). It arrives null until then,
// which is why every consumer keys off `category.id ?? category.label`.
export interface ClinicalReviewCriteria {
  aiCriteriaId: string;
  criterion: string;
  description: string;
  order: number;
  category: { id?: string; name: string; color: string; order: number } | null;
  finding: string | null;
  sources: Array<{ attachmentId: string; displayId: number }>;
}

// Narrow a raw `clinical_review_result` payload — an array of matched criteria — to the typed
// shape. The payload is structured (unlike the scalar-value common case), so read it as unknown
// and validate at runtime — returns [] for a missing payload or any non-array shape, mirroring
// `parsePayerCandidates`.
export function parseClinicalReviewResult(value: unknown): ClinicalReviewCriteria[] {
  return Array.isArray(value) ? (value as ClinicalReviewCriteria[]) : [];
}

// Sort matched criteria into display order: by category `order`, then by each criteria's `order`
// within its category. Uncategorized criteria (null category) sort last. The sort is stable, so
// equal orders keep their payload order. This uses the explicit order the API provides — we no
// longer lean on the array's incidental order (which `groupCriteria` merely preserves).
export function orderClinicalReviewCriteria(criteria: ClinicalReviewCriteria[]): ClinicalReviewCriteria[] {
  const categoryRank = (entry: ClinicalReviewCriteria) => (entry.category ? (entry.category.order ?? 0) : Infinity);
  return [...criteria].sort((a, b) => categoryRank(a) - categoryRank(b) || (a.order ?? 0) - (b.order ?? 0));
}

// The matched criteria for one building's ClinicalReview action, read off the committed workflow
// value for that building. `clinical_review_result` is group-stamped, so resolve it through
// `valueFor` scoped to the action's `groupId` (each building has its own ClinicalReview action and
// its own group-scoped value). The value's content is the criteria array; read it `as unknown`
// because `WorkflowValue.value` is typed for scalars while this kind stores a structured payload
// (the same cast `CompletedResult` uses for its nested-object results).
export function readClinicalReviewResult(workflow: Workflow, action: WorkflowAction): ClinicalReviewCriteria[] {
  const value = workflow.valueFor(CLINICAL_REVIEW_RESULT_VALUE, action.groupId)?.value as unknown;
  return orderClinicalReviewCriteria(parseClinicalReviewResult(value));
}
